import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { V, Haptic } from '../../utils/theme';
import { LS } from '../../utils/storage';
import { Icons } from '../../components/Icons';
import { Card, Btn, Field, Sheet, Chip, Stat, Progress, Skeleton, SkeletonCard, SuccessToastCtrl, ConfirmCtrl, MsgBannerCtrl } from '../../components/ui';
import { today, ago, fmtShort, fmtFull, uid, calc1RM, calcPlates, PLATES, convW, wUnit, dUnit, isCardio, toKg, toLbs, friendDisplayName, chartCfg } from '../../utils/helpers';
import { CloudSync, SocialAPI, SyncQueue, SYNC_URL, APP_VERSION } from '../../utils/sync';
import { apiPost } from '../../utils/api';
import { Undo } from '../../utils/undo';
import { useLayout } from '../../utils/responsive';
import { ShareCard } from '../../utils/share';
import { BADGE_DEFS, calcEarnedBadges } from '../../data/badges';
import { TEMPLATES } from '../../data/templates';
import { IRON_RANKS } from '../../data/ranks';
import { FF_HACKS, FF_BRANDS } from '../../data/hacks';
import { FOODS } from '../../data/foods';
import { AuthToken } from '../../utils/auth';
import { calcReadiness, useStreak, usePRs, useNutritionStreak } from '../../components/dialogs';
import { programDayType } from '../../utils/program';
import { PROGRAMS } from '../../data/programs';
import { builtInSchedule, programProgress } from '../../utils/programEnrollment';
import { normalizeSupplementsFromWire, supplementName } from '../../utils/healthWire';
import { onAccent } from '../../components/kit';

export function FastFoodHacks({s,d}){
  const { isDesktop } = useLayout();
  const [search,setSearch]=useState("");
  const [brand,setBrand]=useState("All");
  const [sort,setSort]=useState("cal"); // cal, protein, name
  const [expanded,setExpanded]=useState(null);

  const brands=["All",...[...new Set(FF_HACKS.map(h=>h.r))].sort()];

  const filtered=useMemo(()=>{
    let list=FF_HACKS;
    if(brand!=="All")list=list.filter(h=>h.r===brand);
    if(search)list=list.filter(h=>h.r.toLowerCase().includes(search.toLowerCase())||h.d.toLowerCase().includes(search.toLowerCase()));
    if(sort==="cal")list=[...list].sort((a,b)=>a.cal-b.cal);
    else if(sort==="protein")list=[...list].sort((a,b)=>(b.p||0)-(a.p||0));
    else list=[...list].sort((a,b)=>a.r.localeCompare(b.r));
    return list;
  },[search,brand,sort]);

  const addToNutrition=(hack)=>{
    const item={name:`${hack.r} Hack`,n:`${hack.r} Hack`,cal:hack.cal||0,protein:hack.p||0,carbs:hack.c||0,fat:hack.f||0};
    const ex=s.nutrition.find(n=>n.date===today());
    if(ex){
      // ADD_N replaces any same-day entry — merge into today's log instead of wiping it
      const meals=(ex.meals||[]).some(m=>m.name==="Fast Food")
        ?(ex.meals||[]).map(m=>m.name==="Fast Food"?{...m,items:[...(m.items||[]),item]}:m)
        :[...(ex.meals||[]),{name:"Fast Food",items:[item]}];
      d({type:"EDIT_N",n:{...ex,cal:(ex.cal||0)+item.cal,protein:(ex.protein||0)+item.protein,carbs:(ex.carbs||0)+item.carbs,fat:(ex.fat||0)+item.fat,meals}});
    }else{
      d({type:"ADD_N",n:{id:uid(),date:today(),cal:item.cal,protein:item.protein,carbs:0,fat:0,fiber:0,water:0,sleep:0,
        meals:[{name:"Fast Food",items:[item]}]}});
    }
    SuccessToastCtrl.show(`${hack.r} hack logged`);
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:18,fontWeight:800,color:V.text}}>Fast Food Hacks</div>
          <div style={{fontSize:11,color:V.text3}}>{filtered.length} macro-friendly meals under 600 cal</div>
        </div>
      </div>

      {/* Search */}
      <div style={{position:"relative"}}>
        <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)"}}>{Icons.search({size:14,color:V.text3})}</div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search restaurants or ingredients..."
          aria-label="Search fast food hacks"
          style={{width:"100%",padding:"10px 14px 10px 34px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
            borderRadius:10,color:V.text,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:V.font}}/>
      </div>

      {/* Sort */}
      <div style={{display:"flex",gap:6}}>
        {[{k:"cal",l:"Lowest Cal"},{k:"protein",l:"Most Protein"},{k:"name",l:"A→Z"}].map(s2=>(
          <button key={s2.k} onClick={()=>setSort(s2.k)} style={{padding:"6px 12px",borderRadius:8,
            border:`1.5px solid ${sort===s2.k?V.accent:V.cardBorder}`,
            background:sort===s2.k?`${V.accent}10`:V.card,
            cursor:"pointer",fontSize:11,fontWeight:600,color:sort===s2.k?V.accent:V.text3,fontFamily:V.font}}>{s2.l}</button>
        ))}
      </div>

      {/* Brand filter — horizontal scroll */}
      <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4,WebkitOverflowScrolling:"touch"}}>
        {brands.map(b=>{
          const info=FF_BRANDS[b];
          return(
            <button key={b} onClick={()=>setBrand(b)} style={{flexShrink:0,padding:"6px 12px",borderRadius:8,
              border:`1.5px solid ${brand===b?(info?.color||V.accent):V.cardBorder}`,
              background:brand===b?`${info?.color||V.accent}15`:V.card,
              cursor:"pointer",fontSize:11,fontWeight:600,color:brand===b?(info?.color||V.accent):V.text3,
              fontFamily:V.font,display:"flex",alignItems:"center",gap:4,whiteSpace:"nowrap"}}>
              {info?.emoji&&<span style={{fontSize:12}}>{info.emoji}</span>}
              {b}
            </button>
          );
        })}
      </div>

      {/* Results */}
      {filtered.map((hack,i)=>{
        const info=FF_BRANDS[hack.r]||{color:V.accent,emoji:"🍽️"};
        const isExpanded=expanded===i;
        return(
          <div key={i} onClick={()=>setExpanded(isExpanded?null:i)} style={{
            background:V.card,border:`1px solid ${V.cardBorder}`,borderRadius:14,
            overflow:"hidden",cursor:"pointer",transition:"all .15s"}}>
            {/* Header */}
            <div style={{padding:"12px 14px",display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:40,height:40,borderRadius:10,background:`${info.color}15`,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>
                {info.emoji}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:V.text,display:"flex",alignItems:"center",gap:6}}>
                  {hack.r}
                </div>
                <div style={{fontSize:11,color:V.text3,marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  {hack.d.split("\n")[0]}
                </div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:16,fontWeight:800,color:V.warn,fontFamily:V.mono}}>{hack.cal}</div>
                <div style={{fontSize:11,color:V.text3,fontWeight:600}}>CAL</div>
              </div>
              {hack.p&&(
                <div style={{textAlign:"right",flexShrink:0,marginLeft:4}}>
                  <div style={{fontSize:14,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{hack.p}g</div>
                  <div style={{fontSize:11,color:V.text3,fontWeight:600}}>PROT</div>
                </div>
              )}
            </div>

            {/* Expanded detail */}
            {isExpanded&&(
              <div style={{padding:"0 14px 14px",borderTop:`1px solid ${V.cardBorder}`}}>
                <div style={{padding:"10px 0",fontSize:12,color:V.text2,lineHeight:1.6,whiteSpace:"pre-line"}}>{hack.d}</div>
                <div style={{display:"flex",gap:8,marginTop:8}}>
                  <button onClick={e=>{e.stopPropagation();addToNutrition(hack);}} style={{flex:1,padding:"10px",borderRadius:10,
                    background:`${V.accent}12`,border:`1px solid ${V.accent}25`,cursor:"pointer",
                    fontSize:11,fontWeight:700,color:V.accent,fontFamily:V.font,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                    {Icons.plus({size:14,color:V.accent})} Log This Meal
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {filtered.length===0&&(
        <div style={{textAlign:"center",padding:30,color:V.text3}}>
          <div style={{fontSize:28,marginBottom:8}}>🔍</div>
          <div style={{fontSize:13}}>No hacks found for "{search||brand}"</div>
        </div>
      )}
    </div>
  );
}

// ─── Meal Plan Generator ───
// ─── Diet / allergy filter for generated plans ───
// Foods are matched by name; the food DB has no diet tags, so this is a conservative keyword filter.
const RX={
  meat:/chicken|beef|steak|pork|turkey|bacon|\bham\b|sausage|lamb|jerky|ground|burger|ribs|brisket|salami|pepperoni|hot dog|meatball|deli|venison|bison|duck|veal|chorizo|\bmeat|ribeye|sirloin|tenderloin|filet|wings|drumstick|thigh|breast|mutton|goat|\belk\b|rabbit|liver|prosciutto|pastrami|corned|gelatin|broth|bone/i,
  fish:/salmon|tuna|shrimp|fish|\bcod\b|tilapia|crab|lobster|sardine|prawn|anchov|mackerel|halibut|oyster|clam|mussel|scallop/i,
  shellfish:/shrimp|crab|lobster|prawn|clam|oyster|mussel|scallop/i,
  pork:/pork|bacon|\bham\b|sausage|salami|pepperoni|prosciutto/i,
  dairy:/milk|cheese|yogurt|yoghurt|whey|butter|cream|casein|cottage|ghee|paneer|kefir/i,
  egg:/\begg/i,
  gluten:/bread|pasta|bagel|tortilla|wheat|noodle|pizza|cereal|cracker|pretzel|waffle|pancake|muffin|flour|croissant|bun\b|toast|couscous|barley|rye/i,
  nuts:/almond|peanut|cashew|walnut|pecan|pistachio|hazelnut|macadamia|\bnut\b|nuts/i,
  soy:/\bsoy|tofu|edamame|tempeh|miso/i,
};
// Names alone miss things like "McNuggets" or "Sea Bass", so animal-protein and
// restaurant-meal categories are treated as animal unless the name is clearly plant-based.
const PLANT=/tofu|tempeh|seitan|lentil|bean|chickpea|edamame|pea protein|plant|soy|quinoa|hummus|falafel|veggie|vegan|vegetable|nut|seed|oat|rice|grain|fruit|salad|avocado|potato|pasta|noodle|bread|toast|wrap|burrito bowl|smoothie/i;
const MORE_ANIMAL=/bass|mahi|nugget|wing|drumstick|steak|filet|fillet|jerky|bison|venison|lamb|duck|goat|crab|lobster|scallop|clam|oyster|mussel|anchov|sardine|mackerel|trout|halibut|snapper|tilapia|swordfish|squid|octopus|shrimp|prawn|salmon|tuna|\bcod\b|burger|meatball|gyro|kebab|taco|quesadilla/i;
const looksAnimal=(f,n)=>RX.meat.test(n)||RX.fish.test(n)||MORE_ANIMAL.test(n)||((f.cat==="Protein"||f.cat==="Meals")&&!PLANT.test(n));
export function foodAllowed(f,diet,allergies=[]){
  const n=f.n||f.name||"";
  if(diet==="Vegetarian"&&looksAnimal(f,n))return false;
  if(diet==="Vegan"&&(looksAnimal(f,n)||RX.dairy.test(n)||RX.egg.test(n)||f.cat==="Dairy"))return false;
  if(diet==="Pescatarian"&&(RX.meat.test(n)||MORE_ANIMAL.test(n)&&!RX.fish.test(n)&&!/bass|mahi|crab|lobster|scallop|clam|oyster|mussel|anchov|sardine|mackerel|trout|halibut|snapper|tilapia|swordfish|squid|octopus|shrimp|prawn|salmon|tuna|cod/i.test(n)))return false;
  if(diet==="Halal"&&RX.pork.test(n))return false;
  if(diet==="Kosher"&&(RX.pork.test(n)||RX.shellfish.test(n)))return false;
  if(allergies.includes("Dairy")&&RX.dairy.test(n))return false;
  if(allergies.includes("Gluten")&&RX.gluten.test(n))return false;
  if(allergies.includes("Nuts")&&RX.nuts.test(n))return false;
  if(allergies.includes("Eggs")&&RX.egg.test(n))return false;
  if(allergies.includes("Shellfish")&&RX.shellfish.test(n))return false;
  if(allergies.includes("Soy")&&RX.soy.test(n))return false;
  return true;
}

export function MealPlanGenerator({s,d}){
  const { isDesktop } = useLayout();
  // The last generated plan survives navigation (it used to vanish on every tab change)
  const [plan,setPlan]=useState(()=>{const saved=LS.get("ft-mealplan");return saved?.date===today()&&Array.isArray(saved.plan)?saved.plan:null;});
  const [logged,setLogged]=useState(false);
  const logPlan=()=>{
    if(!plan||!d)return;
    const toItem=(f)=>({n:f.n,name:f.n,cal:f.cal||0,protein:f.p||0,carbs:f.c||0,fat:f.f||0,fiber:f.fb||0});
    const planMeals=plan.map(m=>({name:m.name,items:m.items.map(toItem)}));
    const sum=(k)=>planMeals.reduce((a,m)=>a+m.items.reduce((b,i)=>b+(i[k]||0),0),0);
    const ex=s.nutrition.find(n=>n.date===today());
    if(ex){
      const meals=planMeals.map(pm=>{const cur=(ex.meals||[]).find(m=>m.name===pm.name);return cur?{...cur,items:[...(cur.items||[]),...pm.items]}:pm;});
      (ex.meals||[]).forEach(m=>{if(!meals.some(x=>x.name===m.name))meals.push(m);});
      d({type:"EDIT_N",n:{...ex,cal:(ex.cal||0)+sum("cal"),protein:(ex.protein||0)+sum("protein"),carbs:(ex.carbs||0)+sum("carbs"),fat:(ex.fat||0)+sum("fat"),meals}});
    } else {
      d({type:"ADD_N",n:{id:uid(),date:today(),cal:sum("cal"),protein:sum("protein"),carbs:sum("carbs"),fat:sum("fat"),fiber:sum("fiber"),water:0,sleep:0,meals:planMeals}});
    }
    setLogged(true);SuccessToastCtrl.show("Plan added to today's log");
  };
  const goals=s.goals||{cal:2400,protein:180,carbs:250,fat:70};
  const diet=s.profile?.diet||null;
  const allergies=useMemo(()=>s.profile?.allergies||[],[s.profile?.allergies]);
  const pantry=useMemo(()=>FOODS.filter(f=>foodAllowed(f,diet,allergies)),[diet,allergies]);
  const filterLabel=[diet,...allergies.map(a=>`no ${a.toLowerCase()}`)].filter(Boolean).join(" · ");
  // Categorize foods by meal type
  const mealFoods={
    Breakfast:pantry.filter(f=>["Dairy","Grains","Fruits","Breakfast"].includes(f.cat)||["Eggs (2 large)","Oatmeal (1 cup)","Greek Yogurt","Banana","Protein Shake","Peanut Butter (2 tbsp)","Whole Wheat Bread (2 slices)","Cottage Cheese (1 cup)"].includes(f.n)),
    Lunch:pantry.filter(f=>["Protein","Legumes","Grains","Veggies"].includes(f.cat)||f.p>=15),
    Dinner:pantry.filter(f=>["Protein","Legumes","Veggies","Grains"].includes(f.cat)||f.p>=20),
    // Snacks are food, not condiments or drinks
    Snacks:pantry.filter(f=>f.cal<=250&&f.cal>=50&&!["Sauces","Drinks","Supplements"].includes(f.cat)),
  };
  const generate=()=>{
    const meals=["Breakfast","Lunch","Dinner","Snacks"];
    const splits=[0.28,0.32,0.3,0.1];
    const result=meals.map((name,i)=>{
      const calTarget=Math.round(goals.cal*splits[i]);
      const pool=(mealFoods[name]||pantry).filter(f=>f.cal<=calTarget*0.6).sort(()=>Math.random()-0.5);
      const items=[];let cal=0,prot=0;
      for(const f of pool){
        if(cal+f.cal<=calTarget*1.1&&items.length<(name==="Snacks"?2:3)&&!items.some(x=>x.n===f.n)){
          items.push(f);cal+=f.cal;prot+=f.p;
        }
        if(items.length>=(name==="Snacks"?2:3))break;
      }
      return{name,items,cal,prot,target:{cal:calTarget,prot:Math.round(goals.protein*splits[i])}};
    });
    setPlan(result);setLogged(false);LS.set("ft-mealplan",{date:today(),plan:result});SuccessToastCtrl.show("Meal plan generated");
  };
  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Daily Meal Plan</div>
      <div style={{fontSize:11,color:V.text3}}>Auto-generated to hit your macro targets: {goals.cal}cal / {goals.protein}g protein</div>
      {filterLabel&&<div style={{fontSize:11,color:V.accent,fontWeight:600}}>Filtered for: {filterLabel} · change in Settings → Profile</div>}
      <div style={{display:"flex",gap:8}}>
        <Btn full onClick={generate}>{Icons.refresh({size:14,color:onAccent()})} {plan?"Regenerate":"Generate Plan"}</Btn>
        {plan&&d&&<Btn v="secondary" full onClick={logPlan} disabled={logged}>{logged?"Added to today":"Add to today's log"}</Btn>}
      </div>
      {plan&&plan.map((meal,i)=>(
        <Card key={i} style={{padding:14}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
            <span style={{fontSize:13,fontWeight:700,color:V.text}}>{meal.name}</span>
            <span style={{fontSize:11,color:V.accent,fontFamily:V.mono}}>{meal.cal}cal / {meal.prot}g{meal.target?<span style={{color:V.text3}}> · target {meal.target.cal}cal / {meal.target.prot}g</span>:null}</span>
          </div>
          {meal.items.map((f,j)=>(
            <div key={j} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:11,
              borderBottom:j<meal.items.length-1?`1px solid ${V.divider}`:"none"}}>
              <span style={{color:V.text2}}>{f.n}</span>
              <span style={{color:V.text3,fontFamily:V.mono}}>{f.cal}cal · {f.p}g</span>
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
}

// ─── Workout Programs Marketplace ───
export { PROGRAMS };
// Program day names → the schedule types the calendar, coach and analytics understand.
// Applying "Upper Power" verbatim used to leave grey calendar cells, an empty exercise
// list and a Full Body fallback in the coach.
// (Lives in utils/program.js so the coach-assigned plan and its Node tests share it.)
export { programDayType };
const EQUIPMENT_LABEL={gym:"a full gym",home:"a home setup",bodyweight:"bodyweight only",machines:"machines only"};

export function ProgramMarketplace({s,d}){
  const { isDesktop } = useLayout();
  const [sel,setSel]=useState(null);
  const availability=parseInt(s.profile?.weeklyAvailability)||0;
  const equipment=s.profile?.equipment||null;
  const doApply=(prog)=>{
    d({type:"SET_SCHEDULE",schedule:builtInSchedule(prog)});
    d({type:"SET_ACTIVE_PROGRAM",activeProgram:{id:prog.id,startDate:today(),week:1,day:0}});
    SuccessToastCtrl.show(`${prog.name} applied to schedule`);
    setSel(null);
  };
  const apply=(prog)=>{
    if(availability&&prog.days>availability){
      ConfirmCtrl.show(`${prog.name} is ${prog.days} days a week`,`You said you can train ${availability} days. It will still be applied to all seven days — you can move sessions in Schedule.`,()=>doApply(prog));
      return;
    }
    doApply(prog);
  };
  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Training Programs</div>
      {s.activeProgram&&<Card>
        <div style={{fontWeight:700,color:V.accent}}>{PROGRAMS.find(p=>p.id===s.activeProgram.id)?.name||s.activeProgram.id}</div>
        <div style={{fontSize:13,color:V.text2,marginTop:6}}>{programProgress(s.activeProgram)}</div>
        <Btn v="ghost" onClick={()=>d({type:"SET_ACTIVE_PROGRAM",activeProgram:null})}>End program</Btn>
      </Card>}
      <div style={{fontSize:11,color:V.text3}}>Pick a program and it auto-populates your weekly schedule.</div>
      {PROGRAMS.map(p=>(
        <Card key={p.id} onClick={()=>setSel(sel===p.id?null:p.id)} style={{padding:14,cursor:"pointer",
          border:sel===p.id?`1.5px solid ${V.accent}`:`1px solid ${V.cardBorder}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:V.text}}>{p.name}</div>
              <div style={{fontSize:11,color:V.text3,marginTop:2}}>{p.weeks} weeks · {p.days} days/week · {p.level}</div>
            </div>
          </div>
          <div style={{fontSize:11,color:V.text2,marginTop:6,lineHeight:1.5}}>{p.desc}</div>
          {(availability&&p.days>availability)||(equipment&&equipment!=="gym")?(
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6}}>
              {availability>0&&p.days>availability&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:6,background:`${V.warn}18`,color:V.warn,fontWeight:700}}>{p.days} days · you have {availability}</span>}
              {equipment&&equipment!=="gym"&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:6,background:`${V.accent2}18`,color:V.accent2,fontWeight:700}}>Built for a full gym · you have {EQUIPMENT_LABEL[equipment]||equipment}</span>}
            </div>
          ):null}
          {sel===p.id&&(
            <div style={{marginTop:10}}>
              <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:10}}>
                {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((day,i)=>(
                  <div key={i} style={{padding:"4px 8px",borderRadius:6,fontSize:11,fontWeight:600,
                    background:p.schedule[i]==="Rest"?V.card:`${V.accent}10`,
                    color:p.schedule[i]==="Rest"?V.text3:V.accent,border:`1px solid ${p.schedule[i]==="Rest"?V.cardBorder:V.accent+"25"}`}}>
                    <div>{day}</div><div style={{fontWeight:700}}>{p.schedule[i]}</div>
                  </div>
                ))}
              </div>
              {/* Exercise details per day */}
              {p.exercises&&(
                <div style={{marginBottom:10}}>
                  {[...new Set(p.schedule.filter(d2=>d2!=="Rest"))].map(day=>(
                    p.exercises[day]&&<div key={day} style={{marginBottom:6}}>
                      <div style={{fontSize:11,fontWeight:700,color:V.accent,marginBottom:2}}>{day}</div>
                      <div style={{fontSize:11,color:V.text3,lineHeight:1.6,paddingLeft:8}}>{p.rotation.find(d2=>d2.name===day)?.exercises.map((e,j)=>`${p.exercises[day][j]} ${e.sets}×${e.repLow?`${e.repLow}–`:""}${e.reps}`).join(" · ")}</div>
                    </div>
                  ))}
                </div>
              )}
              {p.progression==="manual"&&<div style={{fontSize:11,color:V.text3,marginBottom:8}}>Targets are starting points. Adjust main-lift reps and loads for your training-max cycle.</div>}
              <Btn full onClick={()=>apply(p)}>{Icons.check({size:14,color:onAccent()})} Start This Program</Btn>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

// ─── Supplement Tracker ───
export const DEFAULT_SUPPS=["Creatine (5g)","Protein Shake","Multivitamin","Fish Oil","Vitamin D","Pre-Workout","Magnesium","Zinc"];
// Diet from onboarding shapes the starter list (whey and fish oil aren't vegan)
const DIET_SWAPS={vegan:{"Protein Shake":"Plant Protein Shake","Fish Oil":"Algae Omega-3"},vegetarian:{"Fish Oil":"Algae Omega-3"},"plant-based":{"Protein Shake":"Plant Protein Shake","Fish Oil":"Algae Omega-3"}};
export const suppsForDiet=(diet)=>{const map=DIET_SWAPS[String(diet||"").toLowerCase()]||{};return DEFAULT_SUPPS.map(n=>map[n]||n);};
export function defaultSupps(diet){return suppsForDiet(diet).slice(0,4);}

export function SupplementTracker({s,d}){
  const { isDesktop } = useLayout();
  const [supps,setSupps]=useState(()=>{
    const raw=LS.get("ft-supplements");
    if(Array.isArray(raw)&&raw.length)return normalizeSupplementsFromWire(raw);
    return normalizeSupplementsFromWire(defaultSupps(s.profile?.diet));
  });
  // The checklist is keyed by day; re-read it when the app comes back after midnight
  const [dayKey,setDayKey]=useState(today());
  useEffect(()=>{const check=()=>{const t=today();if(t!==dayKey)setDayKey(t);};document.addEventListener("visibilitychange",check);const iv=setInterval(check,60000);return()=>{document.removeEventListener("visibilitychange",check);clearInterval(iv);};},[dayKey]);
  const [checked,setChecked]=useState(()=>LS.get(`ft-supps-${today()}`)||{});
  useEffect(()=>{setChecked(LS.get(`ft-supps-${dayKey}`)||{});},[dayKey]);
  const [newSupp,setNewSupp]=useState("");

  const persistSupps=(next)=>{setSupps(next);LS.set("ft-supplements",next);};
  const toggle=(supp)=>{
    const name=supplementName(supp);
    const next={...checked,[name]:!checked[name]};
    setChecked(next);LS.set(`ft-supps-${dayKey}`,next);
    persistSupps(supps.map(s2=>supplementName(s2)!==name?s2:{
      ...s2,
      takenDates:next[name]
        ? Array.from(new Set([...(s2.takenDates||[]),dayKey])).sort()
        : (s2.takenDates||[]).filter(d=>d!==dayKey),
    }));
    // Mark supplement mission flag if at least one is checked
    if(Object.values(next).some(Boolean))LS.set("ft-supps-logged",today());
  };
  const addSupp=()=>{
    if(!newSupp.trim())return;
    const rec=normalizeSupplementsFromWire([newSupp.trim()])[0];
    persistSupps([...supps,rec]);setNewSupp("");
    SuccessToastCtrl.show("Supplement added");
  };
  const rmSupp=(supp)=>{
    persistSupps(supps.filter(s2=>supplementName(s2)!==supplementName(supp)));
  };
  const doneCount=Object.values(checked).filter(Boolean).length;
  const names=supps.map(supplementName);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:16,fontWeight:800,color:V.text}}>Supplements</div>
          <div style={{fontSize:11,color:V.text3}}>Daily checklist · {doneCount}/{supps.length} taken today</div>
        </div>
        <div style={{fontSize:20,fontWeight:800,color:doneCount===supps.length?V.accent:V.text3,fontFamily:V.mono}}>
          {supps.length>0?Math.round(doneCount/supps.length*100):0}%
        </div>
      </div>
      {/* Progress bar */}
      <div style={{height:4,borderRadius:2,background:V.hoverBg}}>
        <div style={{height:"100%",borderRadius:2,background:V.accent,width:`${supps.length>0?doneCount/supps.length*100:0}%`,transition:"width .3s"}}/>
      </div>
      {supps.map(supp=>{
        const name=supplementName(supp);
        const sub=[supp.dosage,supp.timing].filter(Boolean).join(" · ");
        return(
        <div key={supp.id||name} onClick={()=>toggle(supp)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",
          background:checked[name]?`${V.accent}06`:V.card,border:`1px solid ${checked[name]?V.accent+"20":V.cardBorder}`,
          borderRadius:10,cursor:"pointer"}}>
          <div style={{width:22,height:22,borderRadius:6,border:`2px solid ${checked[name]?V.accent:V.text3}`,
            background:checked[name]?V.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            {checked[name]&&<span style={{color:V.bg,fontSize:12,fontWeight:900}}>✓</span>}
          </div>
          <span style={{flex:1,fontSize:13,color:checked[name]?V.accent:V.text,fontWeight:checked[name]?600:400,
            textDecoration:checked[name]?"line-through":"none"}}>
            {name}{sub?<span style={{fontSize:11,color:V.text3,fontWeight:400,marginLeft:8}}>{sub}</span>:null}
          </span>
          <button onClick={e=>{e.stopPropagation();rmSupp(supp);}} style={{background:"none",border:"none",cursor:"pointer",padding:4}}>
            {Icons.x({size:12,color:V.text3})}
          </button>
        </div>
      );})}
      <div style={{display:"flex",gap:8}}>
        <input value={newSupp} onChange={e=>setNewSupp(e.target.value)} placeholder="Add supplement..."
          onKeyDown={e=>{if(e.key==="Enter")addSupp();}}
          style={{flex:1,padding:"10px 14px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
            borderRadius:10,color:V.text,fontSize:13,outline:"none",fontFamily:V.font}}/>
        <Btn onClick={addSupp} disabled={!newSupp.trim()}>{Icons.plus({size:14,color:onAccent()})}</Btn>
      </div>
      <div style={{fontSize:11,color:V.text3}}>Suggestions: {suppsForDiet(s.profile?.diet).filter(s2=>!names.includes(s2)).slice(0,3).join(", ")}</div>
    </div>
  );
}

// ─── Photo Comparison Slider ───
// ─── Personal Records Page ───
